<template>
  <section class="border-t border-default/60 bg-primary/5">
    <UContainer class="py-12 text-center sm:py-14">
      <h2
        v-if="title"
        class="text-xl font-semibold text-highlighted"
      >
        {{ title }}
      </h2>
      <p
        v-if="subtitle"
        class="mx-auto mt-2 max-w-lg text-sm text-muted"
      >
        {{ subtitle }}
      </p>
      <div
        v-if="!saleId || builderPreview"
        class="mt-6 text-sm text-muted"
      >
        فروش ویژه
      </div>
      <div
        v-else
        class="mx-auto mt-6 max-w-sm space-y-4"
      >
        <p class="text-lg font-bold">
          {{ soldOut ? 'تمام شد' : remainingLabel }}
        </p>
        <p
          v-if="!soldOut"
          class="text-sm text-muted"
        >
          {{ countdown }}
        </p>
        <AuthPhoneInput v-model="phone" />
        <UButton
          block
          :disabled="soldOut || !canClaim"
          :loading="claiming"
          @click="claim"
        >
          {{ ctaLabel || 'دریافت کد' }}
        </UButton>
        <p
          v-if="code"
          class="text-sm font-medium text-primary"
        >
          کد شما: {{ code }}
        </p>
        <p
          v-if="error"
          class="text-sm text-error"
        >
          {{ error }}
        </p>
      </div>
    </UContainer>
  </section>
</template>

<script setup lang="ts">
import AuthPhoneInput from '~/components/auth/PhoneInput.vue'
import { isCompleteIrMobile } from '~/utils/phone'
import { toFaDigits } from '~/utils/faDigits'
import { apiErrorMessage } from '~/utils/apiError'

const props = defineProps<{
  title?: string
  subtitle?: string
  ctaLabel?: string
  saleId?: string
  builderPreview?: boolean
}>()

const phone = ref('')
const claiming = ref(false)
const soldOut = ref(false)
const remaining = ref(0)
const endsAt = ref<Date | null>(null)
const now = ref(Date.now())
const code = ref('')
const error = ref('')
const canClaim = computed(() => isCompleteIrMobile(phone.value) && !claiming.value)
const remainingLabel = computed(() => `${toFaDigits(remaining.value)} ظرفیت باقی‌مانده`)
const countdown = computed(() => {
  if (!endsAt.value) return ''
  const ms = endsAt.value.getTime() - now.value
  if (ms <= 0) return 'مهلت تمام شد'
  const hours = Math.floor(ms / 3_600_000)
  const minutes = Math.floor((ms % 3_600_000) / 60_000)
  return `${toFaDigits(hours)} ساعت و ${toFaDigits(minutes)} دقیقه`
})

onMounted(async () => {
  if (!props.saleId || props.builderPreview) return
  const timer = setInterval(() => { now.value = Date.now() }, 30_000)
  onUnmounted(() => clearInterval(timer))
  try {
    const data = await $fetch<{ item: { slotsRemaining: number; endsAt: string; available: boolean } }>(`/api/campaign/flash-sales/${props.saleId}`)
    remaining.value = data.item.slotsRemaining
    endsAt.value = new Date(data.item.endsAt)
    soldOut.value = !data.item.available || data.item.slotsRemaining <= 0
  }
  catch {
    soldOut.value = true
  }
})

async function claim() {
  if (!props.saleId || !canClaim.value) return
  claiming.value = true
  error.value = ''
  try {
    const data = await $fetch<{ item: { couponCode: string; slotsRemaining: number } }>(`/api/campaign/flash-sales/${props.saleId}/claim`, {
      method: 'POST',
      body: { phone: phone.value },
    })
    code.value = data.item.couponCode
    remaining.value = data.item.slotsRemaining
    if (data.item.slotsRemaining <= 0) soldOut.value = true
  }
  catch (err) {
    error.value = apiErrorMessage(err, 'دریافت کد ناموفق بود')
  }
  finally {
    claiming.value = false
  }
}
</script>
